<template>
    <div style="width: 100%">
        <template v-if="tableData">
            <table style="width: 100%; table-layout: auto; border-spacing: 0">
                <thead>
                    <tr :style="thStyle">
                        <th
                            v-for="(header, index) of tableData.headers"
                            :key="index"
                            :style="thStyle"
                        >
                            {{ header }}
                        </th>
                    </tr>
                </thead>
                <tbody style="text-align: right" :style="tableTheme.rows">
                    <tr v-for="(row, rIndex) of tableData.rows" :key="rIndex">
                        <template v-for="(item, index) of row.values" :key="index">
                            <td :style="tdStyle(row.type, rIndex)">
                                {{ formatCellValue(item) }}
                            </td>
                        </template>
                        <template v-if="emptyColsCount(row)">
                            <td :colspan="emptyColsCount(row)" :style="tdStyle(row.type, rIndex)" />
                        </template>
                    </tr>
                </tbody>
            </table>
        </template>
    </div>
</template>
<script lang="ts">
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";
import { Component, Vue, toNative } from "vue-facing-decorator";

/**
 * tableData format:
 * {
 *  header: [header1, header2, header3],
 *  rows: [ {
 *               type: "normal" | "title" | "subtitle" | "total" | "subtotal",
 *              values: [{text: "Row1", type: "string" | "amount" | "percent" | "number" | null}]
 *          }
 *        ]
 * }
 */
@Component
export class TableComponent extends Vue {
    @Prop({ default: null }) tableData: any;
    @Prop({ default: null }) tableTheme: any;

    beforeMount() {
        console.log("tableTheme", this.tableTheme);
        console.log("tableData", this.tableData);
    }

    @Watch("tableData", { deep: true })
    watch_tableData() {
        console.log("watch_tableData", this.tableData);
    }

    formatTableCellStyle(style): { [key: string]: any } {
        return {
            background: style.background,
            color: style.color,
            textAlign: style.textAlign,
            borderStyle: style.border ? "solid" : null,
            borderWidth: style.border ? "1px" : null,
            borderColor: style.borderColor,
            fontWeight: style.bold ? "bold" : null,
            boxSizing: "border-box",
            borderCollapse: "collapse",
            borderSpacing: 0,
            paddingTop: "2px",
            paddingBottom: "2px",
            paddingLeft: "5px",
            paddingRight: "5px",
        };
    }

    get nCols() {
        return this.tableData.headers?.length ?? 0;
    }

    emptyColsCount(row) {
        return Math.max(0, this.nCols - row.values?.length);
    }

    get thStyle(): { [key: string]: any } {
        const style = this.tableTheme.header ?? {};
        return this.formatTableCellStyle(style);
    }

    tdStyle(rowType, rowIndex) {
        let style = { ...(this.tableTheme.rows ?? {}) };
        if (rowType === "title") {
            style = { ...(this.tableTheme.rows ?? {}), ...(this.tableTheme.title ?? {}) };
        } else {
            // console.log("rowIndex", rowIndex, "style", style)
            if (
                rowIndex != null &&
                rowIndex % 2 == 0 &&
                style.mode === "alternating" &&
                style.secondaryColor
            ) {
                style.background = style.secondaryColor;
            }
        }
        return this.formatTableCellStyle(style);
    }

    formatCellValue(cellItem) {
        if (cellItem == null) {
            return "-";
        }
        switch (cellItem.type) {
            case "amount":
                return newCommonFormatCurrency(cellItem.text);
            case "percent":
                if (cellItem.text != null) {
                    return this.floatingPointToString(+cellItem.text) + "%";
                }
                return "-";
            case "number":
                return cellItem.text ? this.floatingPointToString(+cellItem.text) : "-";
            case "string":
            default:
                return cellItem.text ?? "-";
        }
    }

    /**
     * Check if number locale string formatting is supported
     * @returns true if supported
     */
    protected toLocaleStringSupportsOptions() {
        return !!(typeof Intl === "object" && Intl && typeof Intl.NumberFormat === "function");
    }

    protected floatingPointToString(number) {
        if (typeof number === "number" && this.toLocaleStringSupportsOptions()) {
            const locale = undefined; // "fr-FR"
            return number.toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
        }
        return number;
    }
}

export default toNative(TableComponent);
</script>
<style lang="css" scoped></style>
